import { useAtom, atom } from 'jotai';
import { CroppedImage } from '@components/Train/ImageUpload';
import { FacialHairStyle, HAIR_COLORS, HairStyle, SkinTone } from '@acme/lib';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { Theme } from '@acme/db';
import { AspectRatio, PhotoSize } from '../types/photo';

export const FACIAL_HAIR_COLOR = HAIR_COLORS;

export type HairColor = (typeof HAIR_COLORS)[number];
export type FacialHairColor = (typeof FACIAL_HAIR_COLOR)[number];

export const TRAINING_STORAGE_KEY = 'training-atom-v12';

export type ThemeConfiguration = Omit<
  Theme,
  | 'userId'
  | 'createdAt'
  | 'updatedAt'
  | 'negativeModifiers'
  | 'deletedAt'
  | 'isPublic'
  | 'isPro'
  | 'likeCount'
  | 'category'
  | 'relationship'
  | 'mode'
  | 'accentColor'
  | 'thumbnails'
  | 'meta'
  | 'tags'
  | 'primaryDarkColor'
  | 'secondaryDarkColor'
  | 'lastSampleImageCreatedAt'
> & {
  workflowName?: string;
  photoSize?: {
    aspectRatio: AspectRatio;
    size: PhotoSize;
  };
  quantity?: number;
  selectedModifiers?: Record<string, Record<string, { value: string; color?: string }>>;
  photoQuantity?: number;
  garmentImages?: {
    url: string;
    type: 'top' | 'bottom' | 'one-piece';
  }[];
};

export interface ModelAttributes {
  eyeColor?: string;
  gender?: string;
  glasses?: boolean;
  age?: number;
  hairColor?: HairColor;
  hairStyle?: string;
  skinTone?: SkinTone;
  facialHairStyle?: FacialHairStyle;
  facialHairColor?: FacialHairColor;
}

export interface ModelTrainingState {
  name: string;
  photos: CroppedImage[];
  themes: ThemeConfiguration[];
  selfieUrl: string;
  parentId?: string;
  submittedModelId?: string;
  attributes: ModelAttributes;
}

const initialState: ModelTrainingState = {
  name: '',
  photos: [],
  themes: [],
  selfieUrl: '',
  parentId: undefined,
  submittedModelId: undefined,
  attributes: {
    eyeColor: '',
    gender: '',
    glasses: undefined,
    age: undefined,
    hairStyle: undefined,
    hairColor: undefined,
    skinTone: undefined,
    facialHairStyle: undefined,
    facialHairColor: undefined
  }
};

const storage = createJSONStorage<ModelTrainingState>(() => sessionStorage);
const trainingAtom = atomWithStorage<ModelTrainingState>(TRAINING_STORAGE_KEY, initialState, storage);

// Derived atoms for selective subscriptions
export const trainingBasicInfoAtom = atom(get => {
  const training = get(trainingAtom);
  return {
    name: training.name,
    gender: training.attributes.gender,
    age: training.attributes.age,
    selfieUrl: training.selfieUrl
  };
});

export const trainingAppearanceAtom = atom(get => {
  const training = get(trainingAtom);
  return {
    ...training.attributes
  };
});

export const trainingPhotosAtom = atom(get => {
  const training = get(trainingAtom);
  return {
    photos: training.photos,
    selfieUrl: training.selfieUrl
  };
});

export const trainingThemesAtom = atom(get => {
  const training = get(trainingAtom);
  return {
    themes: training.themes
  };
});

export const useTrainingAtom = () => {
  const [training, setTraining] = useAtom(trainingAtom);

  const setName = (name: string) => {
    setTraining(prev => ({ ...prev, name }));
  };

  const setEyeColor = (eyeColor: string) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, eyeColor }
    }));
  };

  const setGender = (gender: string) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, gender }
    }));
  };

  const setPhotos = (photos: CroppedImage[]) => {
    setTraining(prev => ({ ...prev, photos }));
  };

  const appendPhotos = (newPhotos: CroppedImage[]) => {
    setTraining(prev => {
      const updatedPhotos = [...prev.photos];

      newPhotos.forEach(newPhoto => {
        const existingIndex = updatedPhotos.findIndex(photo => photo.id === newPhoto.id);

        if (existingIndex !== -1) {
          // Update existing photo
          updatedPhotos[existingIndex] = newPhoto;
        } else {
          // Append new photo
          updatedPhotos.push(newPhoto);
        }
      });

      return { ...prev, photos: updatedPhotos };
    });
  };

  const removePhoto = (id: string) => {
    setTraining(prev => ({ ...prev, photos: prev.photos.filter(photo => photo.id !== id) }));
  };

  const removeAllPhoto = () => {
    setTraining(prev => ({ ...prev, photos: [] }));
  };

  const setApiToken = (apiToken: string) => {
    setTraining(prev => ({ ...prev, apiToken }));
  };

  const setThemes = (themes: ThemeConfiguration[]) => {
    setTraining(prev => ({ ...prev, themes }));
  };

  const setGlasses = (glasses: boolean) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, glasses }
    }));
  };

  const setAge = (age: number) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, age }
    }));
  };

  const setSelfieUrl = (image: Omit<CroppedImage, 'id' | 'status'>) => {
    setTraining(prev => {
      // Create a new photo object for the selfie image
      const selfiePhoto: CroppedImage = {
        id: `selfie-${Date.now()}`,
        uploadedUrl: image.uploadedUrl,
        fileName: image.fileName,
        age: image.age,
        originalUrl: image.originalUrl,
        maskedImageUrl: image.maskedImageUrl,
        status: 'success',
        resolution: {
          w: image.resolution?.w || 0,
          h: image.resolution?.h || 0
        },
        caption: image?.caption || 'A photo of TOK'
      };

      // Add to photos array if not already present
      const photoExists = prev.photos.some(photo => photo.uploadedUrl === selfiePhoto.uploadedUrl);
      const updatedPhotos = photoExists ? prev.photos : [selfiePhoto, ...prev.photos];

      return {
        ...prev,
        selfieUrl: selfiePhoto.uploadedUrl!,
        photos: updatedPhotos
      };
    });
  };

  const removeSelfieUrl = () => {
    setTraining(prev => ({ ...prev, selfieUrl: '', photos: [] }));
  };

  const setResolution = (resolution: number) => {
    setTraining(prev => ({ ...prev, resolution }));
  };

  const setBaseModel = (baseModel: string) => {
    setTraining(prev => ({ ...prev, base_model: baseModel }));
  };

  const updateThemePhotoSize = (themeId: string, aspectRatio: AspectRatio, size: PhotoSize) => {
    setTraining(prevTraining => {
      const themeIndex = prevTraining.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevTraining;

      const newState = {
        ...prevTraining,
        themes: prevTraining?.themes?.map((theme, index) =>
          index === themeIndex ? { ...theme, photoSize: { aspectRatio, size } } : theme
        )
      };
      console.log(newState);
      return newState;
    });
  };

  const updateThemeQuantity = (themeId: string, quantity: number) => {
    setTraining(prevTraining => {
      const themeIndex = prevTraining.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevTraining;
      return {
        ...prevTraining,
        themes: prevTraining?.themes?.map((theme, index) => (index === themeIndex ? { ...theme, quantity } : theme))
      };
    });
  };

  const updateThemePhotoQuantity = (themeId: string, photoQuantity: number) => {
    setTraining(prevTraining => {
      const themeIndex = prevTraining.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevTraining;
      return {
        ...prevTraining,
        themes: prevTraining?.themes?.map((theme, index) =>
          index === themeIndex ? { ...theme, photoQuantity } : theme
        )
      };
    });
  };

  const updateThemeSelectedModifiers = (
    themeId: string,
    modifiers: Record<string, Record<string, { value: string; color?: string }>>
  ) => {
    setTraining(prevTraining => {
      const themeIndex = prevTraining.themes?.findIndex(theme => theme.id === themeId);
      if (themeIndex === -1) return prevTraining;
      return {
        ...prevTraining,
        themes: prevTraining?.themes?.map((theme, index) =>
          index === themeIndex
            ? {
                ...theme,
                selectedModifiers: {
                  ...theme.selectedModifiers,
                  ...modifiers
                }
              }
            : theme
        )
      };
    });
  };

  const removeTheme = (themeId: string) => {
    setTraining(prev => ({
      ...prev,
      themes: prev.themes.filter(theme => theme.id !== themeId)
    }));
  };

  const setHairStyle = (hairStyle?: HairStyle) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, hairStyle }
    }));
  };

  const setHairColor = (hairColor?: HairColor) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, hairColor }
    }));
  };

  const setSkinTone = (skinTone: SkinTone) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, skinTone }
    }));
  };

  const setIsRedo = (isRedo: boolean) => {
    setTraining(prev => ({ ...prev, is_redo: isRedo }));
  };

  const clear = () => {
    setTraining({ ...initialState });
  };

  const clearButPreserveSubmission = () => {
    setTraining(prev => ({ ...initialState, submittedModelId: prev.submittedModelId }));
  };

  const updateMultipleAttributes = (updates: Partial<ModelTrainingState>) => {
    setTraining(prev => ({ ...prev, ...updates }));
  };

  const updateImageCaption = (imageId: string, newCaption: string) => {
    setTraining(prev => ({
      ...prev,
      photos: prev.photos.map(photo => (photo.id === imageId ? { ...photo, caption: newCaption } : photo))
    }));
  };

  const setFacialHairStyle = (facialHairStyle?: FacialHairStyle) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, facialHairStyle }
    }));
  };

  const setFacialHairColor = (facialHairColor?: FacialHairColor) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, facialHairColor }
    }));
  };

  const setSubmittedModelId = (modelId?: string) => {
    setTraining(prev => ({ ...prev, submittedModelId: modelId }));
  };

  // New helper function to update multiple attributes at once
  const updateAttributes = (attributeUpdates: Partial<ModelAttributes>) => {
    setTraining(prev => ({
      ...prev,
      attributes: { ...prev.attributes, ...attributeUpdates }
    }));
  };

  return {
    training,
    setTraining,
    setName,
    setEyeColor,
    setGender,
    setPhotos,
    appendPhotos,
    removePhoto,
    setApiToken,
    setThemes,
    setGlasses,
    setAge,
    setSelfieUrl,
    removeSelfieUrl,
    setResolution,
    setBaseModel,
    updateThemePhotoSize,
    updateThemeQuantity,
    updateThemePhotoQuantity,
    updateThemeSelectedModifiers,
    removeTheme,
    setHairStyle,
    setHairColor,
    setSkinTone,
    setIsRedo,
    clear,
    clearButPreserveSubmission,
    removeAllPhoto,
    updateImageCaption,
    setFacialHairStyle,
    setFacialHairColor,
    setSubmittedModelId,
    updateMultipleAttributes,
    updateAttributes
  };
};
